import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, FileText, ChevronLeft, Loader2, AlertCircle } from 'lucide-react';
import { CustomerLookupStep, CustomerVehicleStep } from './customers/CustomerVehiclePicker';
import { buildCustomerPayload, buildVehiclePayload } from '../utils/customerVehicle';
import { createQuote, clearQuoteError } from '../store/slices/quoteSlice';
import useEscapeToClose from './useEscapeToClose';

/**
 * QuoteFormModal — raise a quote for work nobody has booked in yet.
 *
 * Three steps, and the first two are not this file's: `CustomerLookupStep` and
 * `CustomerVehicleStep` are the same components the booking wizard drives, imported
 * from `customers/CustomerVehiclePicker`. That is the whole point — "which customer,
 * and which of their cars" is one problem with one answer, and a quote raised for a
 * walk-in has to handle it exactly as a booking does, down to the rego-ownership
 * warning.
 *
 * Step 3 is deliberately thin. A quote is its priced work items, and those are added
 * in the job detail view afterwards, where the work-item editor, the cost breakdown
 * and the measurement pickers already live. Rebuilding any of that here would be a
 * second editor to keep in step with the first.
 */

const QuoteFormModal = ({ onClose, onCreated }) => {
    const dispatch = useDispatch();
    const { operationLoading, operationError } = useSelector((state) => state.quotes);

    const [step, setStep] = useState(1);
    const [foundCustomer, setFoundCustomer] = useState(null);
    const [selectedVehicleUuid, setSelectedVehicleUuid] = useState(null);
    const [addingNewVehicle, setAddingNewVehicle] = useState(false);
    const [editingVehicleUuid, setEditingVehicleUuid] = useState(null);
    const modalRef = useRef(null);

    const [formData, setFormData] = useState({
        customerType: 'INDIVIDUAL',
        customerName: '',
        customerBusinessName: '',
        customerPhone: '',
        customerEmail: '',
        vehicleRego: '',
        vehicleRegoState: 'VIC',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        vehicleVin: '',
        vehicleEngineNumber: '',
        vehicleTransmission: '',
        description: '',
    });

    useEscapeToClose(true, onClose);

    // Same field-level normalisation the booking form applies, so a rego typed into a
    // quote and the same rego typed into a booking produce the same stored value.
    const handleChange = (e) => {
        const { name, value } = e.target;
        let processed = value;
        if (name === 'vehicleRego') processed = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (name === 'vehicleVin') processed = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (name === 'vehicleEngineNumber') processed = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (name === 'customerPhone') processed = value.replace(/[^\d\s\-()]/g, '');
        setFormData((prev) => ({ ...prev, [name]: processed }));
    };

    const handleCustomerFound = (customer, searchedRego = null) => {
        setFoundCustomer(customer);

        // Same pre-selection the booking form does: match the rego that was searched
        // for, else take the only vehicle, else make staff choose. A customer with one
        // car should never have to pick it.
        const vehicles = customer.vehicles ?? [];
        let preselected = null;
        if (searchedRego && vehicles.length > 0) {
            const needle = searchedRego.toUpperCase().replace(/[\s\-]/g, '');
            preselected = vehicles.find((v) =>
                (v.regoNumber ?? '').toUpperCase().replace(/[\s\-]/g, '').includes(needle));
        }
        if (!preselected && vehicles.length === 1) preselected = vehicles[0];
        setSelectedVehicleUuid(preselected ? preselected.uuid : null);
        setStep(2);
    };

    const handleSkipLookup = ({ prefill = {} }) => {
        setFoundCustomer(null);
        setSelectedVehicleUuid(null);
        setAddingNewVehicle(!!prefill.vehicleRego);
        setFormData((prev) => ({
            ...prev,
            ...(prefill.customerPhone && { customerPhone: prefill.customerPhone }),
            ...(prefill.customerEmail && { customerEmail: prefill.customerEmail }),
            ...(prefill.customerName && { customerName: prefill.customerName }),
            ...(prefill.vehicleRego && { vehicleRego: prefill.vehicleRego }),
        }));
        setStep(2);
    };

    const handleSubmit = async () => {
        dispatch(clearQuoteError());
        try {
            const created = await dispatch(createQuote({
                customer: buildCustomerPayload({ foundCustomer, formData }),
                vehicle: buildVehiclePayload({ selectedVehicleUuid, addingNewVehicle, formData }),
                description: formData.description?.trim() || null,
            })).unwrap();
            // Hand the new quote straight back so the board can open it in the job
            // detail view — a quote with no work items is not yet a quote, and making
            // staff find the card they just created to add them is a wasted step.
            onCreated?.(created);
        } catch {
            // Surfaced from operationError below, verbatim from the server.
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div ref={modalRef} className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
                <div className="px-5 py-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
                            <FileText size={18} className="text-violet-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">New Quote</h2>
                            <p className="text-xs text-gray-500">
                                Price work before the car is booked in
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                {step === 1 && (
                    <CustomerLookupStep onFound={handleCustomerFound} onSkip={handleSkipLookup} />
                )}

                {step === 2 && (
                    <CustomerVehicleStep
                        customerData={foundCustomer}
                        vehicles={foundCustomer?.vehicles ?? []}
                        selectedVehicleUuid={selectedVehicleUuid}
                        setSelectedVehicleUuid={setSelectedVehicleUuid}
                        addingNewVehicle={addingNewVehicle}
                        setAddingNewVehicle={setAddingNewVehicle}
                        formData={formData}
                        onChange={handleChange}
                        isEdit={false}
                        onBack={() => setStep(1)}
                        onNext={() => setStep(3)}
                        editingVehicleUuid={editingVehicleUuid}
                        setEditingVehicleUuid={setEditingVehicleUuid}
                        anchorRef={modalRef}
                    />
                )}

                {step === 3 && (
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                What is being quoted?
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                maxLength={2000}
                                placeholder="e.g. Customer reports a grinding noise from the front brakes — quote for pads and rotors"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            />
                            <p className="text-xs text-gray-500 mt-1.5">
                                Priced work items are added next, on the quote itself.
                            </p>
                        </div>

                        {operationError && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                                <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                                {/* The server names what is wrong — a vehicle owned by
                                    someone else, a customer that needs an email. A generic
                                    message would throw that away. */}
                                <p className="text-sm text-red-700">{operationError}</p>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-2">
                            <button type="button" onClick={() => setStep(2)}
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl">
                                <ChevronLeft size={16} /> Back
                            </button>
                            <button type="button" onClick={handleSubmit} disabled={operationLoading}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-semibold hover:bg-violet-700 disabled:opacity-50">
                                {operationLoading && <Loader2 size={15} className="animate-spin" />}
                                Create quote
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuoteFormModal;
